import React, { useEffect, useState } from "react";
import MainLayoutWithoutFooter from "../components/MainLayoutWithoutFooter";
import AddPromotionModal from "../components/modals/AddPromotionModal";
import AuthorizationPinModal from "../components/modals/AuthorizationPinModal";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import {
  eligibleNowState,
  readOfferScheduleFlag,
  scheduleCellText,
  targetsCellText,
} from "../utils/offerSchedule";
import { USING_PROMO_SCHEDULE_MOCK, mockPromoApi } from "../utils/promoScheduleService";
import { validityState } from "../utils/promoEdit";

const Promotions = () => {
  const [promos, setPromos] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    pageCount: 1,
    perPage: 20,
  });
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  // The promotion being edited (a listing row), or null when the modal is the Add one.
  const [editingPromo, setEditingPromo] = useState(null);
  // The authorizing PIN (a manager's staff PIN or the restaurant's super admin PIN), held in
  // memory only for as long as the Add Promotion modal is open. The server is the authority: it
  // validates the PIN on create-offer, so this screen only collects it.
  const [authPin, setAuthPin] = useState(null);
  const [pinPromptOpen, setPinPromptOpen] = useState(false);
  const [pinError, setPinError] = useState("");
  // Server-driven: true only when the listing says features.offer_schedule for this restaurant.
  // False/absent (old backend, flag off) leaves this screen and the modal exactly as they were.
  const [offerSchedule, setOfferSchedule] = useState(false);

  const fetchPromotions = async (page = 1) => {
    setLoading(true);

    try {
      // include_expired: an expired or not-yet-started promotion must stay findable so it can be edited
      // (typically to extend its dates); each row's badge says when it is not live.
      const url = `${apiUrls.PROMO_LISTING}?page=${page}&include_expired=1`;
      const res = USING_PROMO_SCHEDULE_MOCK ? await mockPromoApi("listing") : await callAPI(url,{}, "GET");
      console.log(res);
      const data = res.data || {};

      setPromos(data.data || []);
      setOfferSchedule(readOfferScheduleFlag(data));
      setMeta(
        data._meta || { currentPage: 1, pageCount: 1, perPage: 20 }
      );
    } catch {
      alert("Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions(meta.currentPage);
  }, [meta.currentPage]);

  const closeAddModal = () => {
    setShowAddModal(false);
    setEditingPromo(null);
    setAuthPin(null);
  };

  // Everyone is asked for a PIN on every press of Add New - restaurants with staff PIN login off
  // too, and managers already signed in too - because the server needs a PIN in the create request
  // and does not trust this client's say-so about who is on the till. The PIN is NOT pre-checked
  // here: the staff-PIN endpoint only knows employee PINs and would wrongly reject a super admin
  // PIN, so the prompt just collects it and the server's answer (403 + message) is shown as-is.
  const handleAddClick = () => {
    setEditingPromo(null);
    setAuthPin(null);
    setPinError("");
    setPinPromptOpen(true);
  };

  // Edit asks for the same PIN as Add - the server checks it again on update-offer.
  const handleEditClick = (promo) => {
    setEditingPromo(promo);
    setAuthPin(null);
    setPinError("");
    setPinPromptOpen(true);
  };

  const handlePinEntered = (pin) => {
    setPinPromptOpen(false);
    setAuthPin(pin);
    setShowAddModal(true);
  };

  // The server refused the PIN on save: keep the form, say why, and ask for the PIN again.
  const handlePinRejected = (message) => {
    setAuthPin(null);
    setPinError(message || "");
    setPinPromptOpen(true);
  };

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent customersPage pb-3">
        <div className="container">

          {/* HEADER */}
          <div className="sbRow mb-3">
            <h2 className="lgTitle mb-0">Promotions</h2>

            <button
              type="button"
              className="btn iconBtn btnPrimary"
              onClick={handleAddClick}
            >
              Add New
            </button>
          </div>

          {/* TABLE */}
          {loading ? (
            <div>Loading...</div>
          ) : (
            <div className="cardStyle">
              <table className="table tableStyle2">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Promo Code</th>
                    <th>Type</th>
                    <th>One-Time Use</th>
                    <th>Description</th>
                    {offerSchedule && <th>Schedule</th>}
                    {offerSchedule && <th>Applies to</th>}
                    {!USING_PROMO_SCHEDULE_MOCK && <th></th>}
                  </tr>
                </thead>

                <tbody>
                  {promos.length ? (
                    promos.map((p, i) => (
                      <tr key={i}>
                        <td>
                          {(meta.currentPage - 1) * meta.perPage + i + 1}
                        </td>
                        <td>
                          {p.promocode}
                          {validityState(p) === "expired" && <span className="ms-2 badge bg-danger">Expired</span>}
                          {validityState(p) === "scheduled" && <span className="ms-2 badge bg-info">Not started</span>}
                        </td>
                        <td>{p.is_coupon === 1 ? "Voucher" : "Promo"}</td>
                        <td>{p.one_time_use_only === 1 ? "Yes" : "No"}</td>
                        <td>{p.details}</td>
                        {/* Vouchers (is_coupon) are redeemed by code and are not subject to schedule/target rules. */}
                        {offerSchedule && (
                          <td>
                            {p.is_coupon === 1 ? "-" : scheduleCellText(p)}
                            {p.is_coupon !== 1 && eligibleNowState(p) !== null && (
                              <span
                                className={`ms-2 badge ${eligibleNowState(p) ? "bg-success" : "bg-secondary"}`}
                              >
                                {eligibleNowState(p) ? "Active now" : "Not active now"}
                              </span>
                            )}
                          </td>
                        )}
                        {offerSchedule && <td>{p.is_coupon === 1 ? "-" : targetsCellText(p)}</td>}
                        {!USING_PROMO_SCHEDULE_MOCK && (
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditClick(p)}
                            >
                              Edit
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={(offerSchedule ? 7 : 5) + (USING_PROMO_SCHEDULE_MOCK ? 0 : 1)} className="text-center">
                        No promotions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* PAGINATION */}
              <div className="d-flex justify-content-center gap-3 mt-3">
                <button
                  disabled={meta.currentPage === 1}
                  onClick={() =>
                    setMeta((p) => ({
                      ...p,
                      currentPage: p.currentPage - 1,
                    }))
                  }
                >
                  Prev
                </button>

                <span>
                  Page {meta.currentPage} of {meta.pageCount}
                </span>

                <button
                  disabled={meta.currentPage === meta.pageCount}
                  onClick={() =>
                    setMeta((p) => ({
                      ...p,
                      currentPage: p.currentPage + 1,
                    }))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddPromotionModal
        isOpen={showAddModal}
        onClose={closeAddModal}
        onCreated={() => fetchPromotions(1)}
        promo={editingPromo}
        authPin={authPin}
        onPinRejected={handlePinRejected}
        offerScheduleEnabled={offerSchedule}
      />
      <AuthorizationPinModal
        isOpen={pinPromptOpen}
        onCancel={() => {
          setPinPromptOpen(false);
          // Cancelling the first prompt abandons the edit; cancelling a re-prompt after a refused PIN
          // leaves the modal (and what was typed into it) open, so it must keep its promotion.
          if (!showAddModal) setEditingPromo(null);
        }}
        onConfirm={handlePinEntered}
        title="Authorization Required"
        subtitle={`Enter a manager PIN or the restaurant super admin PIN to ${editingPromo ? "edit" : "add"} a promotion`}
        error={pinError}
      />
    </MainLayoutWithoutFooter>
  );
};

export default Promotions;